import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  motion,
  useMotionValue,
  useAnimation,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import Card from "../common/Card";

// --- Configuration Constants ---
const AUTO_ADVANCE_MS = 2000;
const DRAG_THRESHOLD = 80;
const SPRING_PARAMS = {
  type: "spring",
  stiffness: 220,
  damping: 20,
};

// --- Image Imports (Build-time) ---
// Ensure these files exist in src/assets/routes/
// If bundle size becomes an issue, move images to public/ and use URL paths instead.
import img1_1x from "../../assets/routes/1-mumbai-coastal-circuit@1x.png";
import img1_2x from "../../assets/routes/1-mumbai-coastal-circuit@2x.png";
import img2_1x from "../../assets/routes/2-delhi-heritage-trail@1x.png";
import img2_2x from "../../assets/routes/2-delhi-heritage-trail@2x.png";
import img3_1x from "../../assets/routes/3-gandhi-colony-to-rps@1x.png";
import img3_2x from "../../assets/routes/3-gandhi-colony-to-rps@2x.png";
import img4_1x from "../../assets/routes/4-chohal-lake@1x.png";
import img4_2x from "../../assets/routes/4-chohal-lake@2x.png";

// --- Data ---
// EDIT HERE: Update 'name' and 'distance' for each route.
const ROUTES = [
  {
    id: 1,
    name: "Mumbai Coastal Circuit",
    distance: "35 km",
    img1x: img1_1x,
    img2x: img1_2x,
  },
  {
    id: 2,
    name: "Delhi Heritage Trail",
    distance: "18 km",
    img1x: img2_1x,
    img2x: img2_2x,
  },
  {
    id: 3,
    name: "Gandhi Colony to RPS",
    distance: "24 km",
    img1x: img3_1x,
    img2x: img3_2x,
  },
  {
    id: 4,
    name: "Chohal Lake Ride",
    distance: "12 km",
    img1x: img4_1x,
    img2x: img4_2x,
  },
];

const RoutesStack = ({ className = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isDragging = useRef(false);
  const isHovering = useRef(false);
  const timerRef = useRef(null);

  // --- Auto-advance Logic ---
  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      if (!isDragging.current && !isHovering.current) {
        nextCard();
      }
    }, AUTO_ADVANCE_MS);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    startTimer();
    return () => stopTimer();
  }, [startTimer, stopTimer]);

  // --- Navigation Helpers ---
  const nextCard = () => {
    setCurrentIndex((prev) => (prev + 1) % ROUTES.length);
  };

  const prevCard = () => {
    setCurrentIndex((prev) => (prev - 1 + ROUTES.length) % ROUTES.length);
  };

  // --- Interaction Handlers ---
  const handleDragStart = () => {
    isDragging.current = true;
    stopTimer();
  };

  const handleDragEnd = (event, info) => {
    isDragging.current = false;
    const offset = info.offset.x;

    if (offset < -DRAG_THRESHOLD) {
      nextCard();
    } else if (offset > DRAG_THRESHOLD) {
      prevCard();
    }

    // Restart timer after a short delay to allow transition to settle
    setTimeout(startTimer, 500);
  };

  const handleMouseEnter = () => {
    isHovering.current = true;
    stopTimer();
  };

  const handleMouseLeave = () => {
    isHovering.current = false;
    startTimer();
  };

  const handleCardClick = () => {
    // Only trigger click if not dragging (simple heuristic can be drag distance check often handled by DragEnd,
    // but here we just rely on standard click. If dragging happened, dragEnd usually consumes event).
    // Using a safe DOM check:
    const mapElement = document.getElementById("routes-section");
    if (mapElement) {
      mapElement.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      console.warn('RoutesStack: Element with id="routes-section" not found.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  // --- Stacking Math ---
  // We want to verify `i` relative to `currentIndex`.
  // Relative index: 0 = center, 1 = behind/right, -1 = behind/left (or simply hidden/ cycled).
  // We need to map the circular array to linear slots for animation.
  // Standard trick: render all, but calculate their visual 'slot'.

  const getCardStyle = (index) => {
    // Calculate the logical offset considering wrap-around
    let offset = (index - currentIndex) % ROUTES.length;
    if (offset < 0) offset += ROUTES.length;

    // We want centered at 0.
    // Let's define slots: 0 (active), 1 (next), etc.
    // However, if we just cycle 0..N-1, it jumps.
    // Better approach for small N=4:
    // Determine the "visual index" relative to current.
    // If we have [0, 1, 2, 3] and current is 3:
    // 3 is pos 0. 0 is pos 1. 1 is pos 2. 2 is pos 3 (or -1).

    // Let's re-calculate signed distance for wrapping:
    // dist = index - currentIndex
    // correct for wrapping to find shortest path, but here we explicitly want a "stack"
    // where items enter from right/bottom stack and leave to left.
    // Actually, a simple stack usually flows: Active (0) -> Next (1) -> Next (2)...
    // When Active becomes Prev, it should fly away or fade.

    // Let's use a simpler "slot" mapping:
    // slot 0 = Front
    // slot 1 = Middle
    // slot 2 = Back
    // slot 3 = Way back (hidden/fading)

    let slot = (index - currentIndex + ROUTES.length) % ROUTES.length;

    // Z-Index: Front is highest.
    const zIndex = 10 - slot;

    // Configuration for slots
    // Slot 0: Center, Full scale
    // Slot 1: Right 30px, Scale 0.9, Opacity 1
    // Slot 2: Right 60px, Scale 0.8, Opacity 0.5
    // Slot 3: Right 90px, Scale 0.7, Opacity 0 (or very low)

    // Tweaking visuals for "Stack" look (top-right or just right stacking?)
    // Requirement: "center card highest, next card slightly to the right with smaller scale"

    let x = 0;
    let scale = 1;
    let opacity = 1;
    let rotate = 0;

    if (slot === 0) {
      x = 0;
      scale = 1;
      opacity = 1;
      rotate = 0;
    } else if (slot === 1) {
      x = 40; // px
      scale = 0.92;
      opacity = 0.8;
      rotate = 3;
    } else if (slot === 2) {
      x = 80; // px
      scale = 0.84;
      opacity = 0.5;
      rotate = 6;
    } else {
      // The last card (about to wrap or just hidden)
      x = 120;
      scale = 0.76;
      opacity = 0; // completely hide the jumper
      rotate = 9;
    }

    return { x, scale, opacity, zIndex, rotate };
  };

  return (
    <div
      className={`hero-stack relative w-full h-full flex items-center justify-center ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative w-full h-full max-w-[500px] max-h-[600px] flex items-center justify-center">
        {ROUTES.map((route, index) => {
          const style = getCardStyle(index);
          const isCurrent =
            (index - currentIndex + ROUTES.length) % ROUTES.length === 0;

          return (
            <motion.div
              key={route.id}
              className="absolute origin-center cursor-pointer will-change-transform w-[280px] md:w-[340px] lg:w-[360px]"
              initial={false}
              animate={{
                x: style.x,
                scale: style.scale,
                opacity: style.opacity,
                zIndex: style.zIndex,
                rotate: style.rotate,
              }}
              whileHover={
                isCurrent
                  ? { y: -6, scale: 1.02, transition: { duration: 0.2 } }
                  : {}
              }
              transition={SPRING_PARAMS}
              drag={isCurrent ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.05} // Subtle resistance
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              aria-label={`View details for ${route.name}, ${route.distance}`}
            >
              <Card
                className="w-full h-full flex flex-col overflow-hidden bg-white dark:bg-gray-900 !border-[10px] !border-white dark:!border-gray-900 transition-colors duration-300"
                style={{ padding: 0 }}
              >
                {/* Image Container with aspect ratio */}
                <div
                  className="relative w-full bg-gray-200 dark:bg-gray-800 rounded-t-xl overflow-hidden"
                  style={{ aspectRatio: "3/4" }}
                >
                  <img
                    src={route.img1x}
                    srcSet={`${route.img1x} 1x, ${route.img2x} 2x`}
                    alt={route.name}
                    className="w-full h-full object-cover pointer-events-none" // prevent dragging image itself ghosting
                  />
                </div>

                {/* Card Footer/Details */}
                <div className="p-4 bg-white dark:bg-gray-900 rounded-b-xl shadow-lg border-t border-gray-50 dark:border-gray-800 flex flex-col items-center transition-colors duration-300">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {route.name}
                  </h3>
                  <p className="text-sm font-medium text-green-600 mt-1">
                    {route.distance}
                  </p>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default RoutesStack;
